import {
  PAYMENT_GATEWAY,
  PLAN_GROUP_FEATURE_GATING_MODE,
  PLAN_BILLING_GATEWAY,
  type FeatureKeyLike,
  type PlanFeature,
} from '@adtrackify/at-tracking-event-types';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { resolveFeatureEnabled } from '../helpers/feature-scope-utils';

import { usePlans } from '@/features/billing/hooks/use-plans';
import { useAccountState } from '@/contexts/account/account-context';
import { SubscriptionsApiService } from '@/services/internal-api/subscriptions/subscriptions-api-service';
import type {
  UpgradePath,
  FeatureUpgradePathResult,
} from '../types/use-feature-upgrade-path-types';
import * as Logger from '@/lib/monitoring/logger';

interface PlanFeaturesData {
  features: PlanFeature[];
  effectiveGatingMode: PLAN_GROUP_FEATURE_GATING_MODE;
}

export function useFeatureUpgradePath(featureKey: FeatureKeyLike): FeatureUpgradePathResult {
  const { currentAccount } = useAccountState();
  const subscription = currentAccount?.subscription;
  const currentPlan = subscription?.subscriptionPlan;
  const currentPlanGroupName = subscription?.subscriptionPlanGroup?.displayName;
  const { plans, isLoading: plansLoading } = usePlans();

  const upgradeCandidates = useMemo(() => {
    if (!plans) return [];
    return plans.filter(p =>
      p.isActive &&
      p.isPublic &&
      p.billingGateway === PLAN_BILLING_GATEWAY.PLATFORM &&
      (p.planSchemaVersion ?? 1) >= 2 &&
      p.id !== currentPlan?.id,
    );
  }, [plans, currentPlan?.id]);

  const candidateIds = useMemo(
    () => upgradeCandidates.map(p => p.id),
    [upgradeCandidates],
  );

  const { data: planFeaturesMap, isLoading: featuresLoading } = useQuery({
    queryKey: ['plan-features-bulk', candidateIds.sort().join(',')],
    queryFn: async () => {
      const results: Record<string, PlanFeaturesData> = {};
      await Promise.all(
        candidateIds.map(async (planId) => {
          try {
            const { data } = await SubscriptionsApiService.getPlanFeatures(planId);
            results[planId] = {
              features: data.planFeatures ?? [],
              effectiveGatingMode: data.effectiveGatingMode ?? PLAN_GROUP_FEATURE_GATING_MODE.DENY_LIST,
            };
          } catch (error) {
            Logger.warn('Failed to load plan features for upgrade path', { error, planId });
            results[planId] = {
              features: [],
              effectiveGatingMode: PLAN_GROUP_FEATURE_GATING_MODE.DENY_LIST,
            };
          }
        }),
      );
      return results;
    },
    enabled: candidateIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const result = useMemo((): FeatureUpgradePathResult => {
    if (plansLoading || featuresLoading || !planFeaturesMap) {
      return { availablePlans: [], isLoading: true, currentPlan, currentPlanGroupName };
    }

    const currentPrice = currentPlan?.price ?? 0;

    const available: UpgradePath[] = [];
    for (const plan of upgradeCandidates) {
      const planData = planFeaturesMap[plan.id];
      if (!planData) continue;
      const { features, effectiveGatingMode } = planData;
      const { isEnabled } = resolveFeatureEnabled(featureKey, features, effectiveGatingMode, PAYMENT_GATEWAY.STRIPE);
      if (!isEnabled) continue;
      available.push({
        plan,
        monthlyPrice: plan.displayPrices?.monthly ?? plan.displayPrice,
        isUpgrade: plan.price > currentPrice,
      });
    }
    available.sort((a, b) => a.plan.price - b.plan.price);

    return {
      availablePlans: available,
      recommendedPlan: available[0],
      currentPlan,
      currentPlanGroupName,
      isLoading: false,
    };
  }, [plansLoading, featuresLoading, planFeaturesMap, upgradeCandidates, featureKey, currentPlan, currentPlanGroupName]);

  return result;
}
